import { ApolloClient, type FieldMergeFunction, InMemoryCache } from "@apollo/client/core";
import UploadHttpLink from "apollo-upload-client/UploadHttpLink.mjs";
import { sessionCheckedFetch } from "./sessionFetch";

const GRAPHQL_ENDPOINT = import.meta.env.VITE_API_URL || "http://localhost:4001/graphql";

if (!import.meta.env.VITE_API_URL) {
  console.warn("⚠️ VITE_API_URL not set, using default: http://localhost:4001/graphql");
}

const uploadLink = new UploadHttpLink({
  uri: GRAPHQL_ENDPOINT,
  credentials: "include", // Send cookies with requests
  headers: {
    "apollo-require-preflight": "true", // CSRF protection header
  },
  fetch: sessionCheckedFetch,
});

type Edge = { node: { __typename?: string; [key: string]: unknown }; cursor: string };
type Connection = { edges: Edge[]; pageInfo: { hasNextPage: boolean; endCursor: string | null } };

const mergeDeduplicated: FieldMergeFunction<Connection> = (existing, incoming, { readField }) => {
  if (!existing) return incoming;

  const mergedEdges = [...existing.edges];
  const existingIdMap = new Map<string, number>();

  mergedEdges.forEach((edge, index) => {
    // biome-ignore lint/suspicious/noExplicitAny: Apollo readField requires flexible type
    const id = readField<string>("id", edge.node as any);
    if (id) {
      existingIdMap.set(id, index);
    }
  });

  for (const edge of incoming.edges) {
    // biome-ignore lint/suspicious/noExplicitAny: Apollo readField requires flexible type
    const id = readField<string>("id", edge.node as any);
    if (id && existingIdMap.has(id)) {
      const index = existingIdMap.get(id);
      if (index !== undefined) {
        mergedEdges[index] = edge;
      }
    } else if (id) {
      mergedEdges.push(edge);
    }
  }

  return {
    ...incoming,
    edges: mergedEdges,
  };
};

export const apolloClient = new ApolloClient({
  link: uploadLink,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          mediaHouseMedia: {
            keyArgs: ["folderId", "type"],
            merge: mergeDeduplicated, // Use the smart merge
          },
          mediaHouseFolders: {
            keyArgs: ["parentId", "type"],
            merge: mergeDeduplicated, // Use the smart merge
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
    mutate: {
      // Prevent intermediate renders during refetches
      awaitRefetchQueries: true,
    },
  },
});
